import { useCallback, type RefObject } from "react";
import { toast } from "sonner";
import type { useZero } from "@rocicorp/zero/react";
import { mutators } from "@/lib/mutators";
import { markSent } from "@/lib/send-trace";
import { captureSentryException } from "@/lib/sentry";
import type { TiptapEditorRef } from "../TiptapEditor";

interface UseGiphySendArgs {
  channelId: string;
  threadId?: string;
  replyingToMessageId?: string | null;
  onClearReply?: () => void;
  user: { id: string } | null | undefined;
  zero: ReturnType<typeof useZero>;
  editorRef: RefObject<TiptapEditorRef | null>;
  setGiphyPreview: (next: { searchTerm: string } | null) => void;
  onSend?: () => void;
}

/**
 * Giphy send + cancel handlers. Extracted from `useMessageSend` to keep
 * that hook under the 400-line ceiling. Routes through the same
 * caller-explicit mutator selection as text sends:
 *   threadId set            → addReply (sidebar)
 *   replyingToMessageId set → addInlineReply (channel composer)
 *   neither                 → insert (top-level)
 */
export function useGiphySend({
  channelId,
  threadId,
  replyingToMessageId,
  onClearReply,
  user,
  zero,
  editorRef,
  setGiphyPreview,
  onSend,
}: UseGiphySendArgs) {
  const handleGiphySend = useCallback(
    async (gifUrl: string, searchTerm: string) => {
      if (!user) return;
      const messageId = crypto.randomUUID();
      const now = Date.now();
      const payload = { url: gifUrl, search_term: searchTerm };
      const replyParentId = threadId ?? replyingToMessageId ?? null;
      const isInlineReply = !threadId && !!replyingToMessageId;
      try {
        if (replyParentId) {
          markSent(
            messageId,
            channelId,
            threadId ? "giphy-addReply" : "giphy-inline-addReply",
          );
          const replyBody = {
            id: messageId,
            user_id: user.id,
            content: "",
            kind: "gif" as const,
            payload,
            is_edited: false,
            created_at: now,
            updated_at: now,
          };
          const result = zero.mutate(
            isInlineReply
              ? mutators.messages.addInlineReply({
                  parent_id: replyParentId,
                  reply: replyBody,
                })
              : mutators.messages.addReply({
                  parent_id: replyParentId,
                  reply: replyBody,
                }),
          );
          const res = await result.client;
          if (res.type === "error") {
            throw new Error(
              `giphy addReply failed: ${res.error instanceof Error ? res.error.message : String(res.error)}`,
            );
          }
          if (!threadId && replyingToMessageId) onClearReply?.();
        } else {
          markSent(messageId, channelId, "giphy-insert");
          const result = zero.mutate(
            mutators.messages.insert({
              id: messageId,
              channel_id: channelId,
              user_id: user.id,
              content: "",
              kind: "gif",
              payload,
              is_edited: false,
              is_broadcast: false,
              created_at: now,
              updated_at: now,
            }),
          );
          const res = await result.client;
          if (res.type === "error") {
            throw new Error(
              `giphy insert failed: ${res.error instanceof Error ? res.error.message : String(res.error)}`,
            );
          }
        }

        setGiphyPreview(null);
        onSend?.();
      } catch (err) {
        console.error("[message-input] giphy send failed:", err);
        captureSentryException(err, {
          tag: "giphy-send",
          extra: { channelId, hasReplyParent: !!replyParentId },
        });
        toast.error("Couldn't send the gif. Try again.");
      }
    },
    [
      user,
      channelId,
      threadId,
      replyingToMessageId,
      onClearReply,
      zero,
      onSend,
      setGiphyPreview,
    ],
  );

  const handleGiphyCancel = useCallback(() => {
    setGiphyPreview(null);
    editorRef.current?.focus();
  }, [editorRef, setGiphyPreview]);

  return { handleGiphySend, handleGiphyCancel };
}
